import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";
import { Dispatch } from "redux";
import {
	Actions,
	CallBackProps,
	Events,
	Origin,
	Status,
	STATUS,
	ACTIONS,
	EVENTS,
	ORIGIN,
	Step,
} from "react-joyride";

import { AppState, setHeaderState, setPageState, setTriggerOnboarding } from "../../store/reducer";
import headerImage from "../../assets/images/banner_nurasign.png";
import { HeaderState } from "../../components/Header/HeaderUtils";
import { LoggedUser } from "../../store/interfaces";
import { Position } from "../Login/LoginUtils";
import { PageState } from "./HomeUtils";

interface HomeContextType {
	joyrideRef: React.RefObject<any>;
	joyrideCallback: (data: any) => void;
	runOnboarding: boolean;
	stepIndex: number;
	steps: any[];
	currentVariantSidebar: any; // Replace 'any' with the actual type
	pageState: PageState;
	openAssignModal: boolean;
}

const HomeContext = createContext<HomeContextType | null>(null);

export function useHomeContext() {
	const context = useContext(HomeContext);
	if (!context) {
		throw new Error("useHomeContext debe usarse dentro de <HomeProvider>");
	}

	return context;
}

export function HomeProvider({ children }: { children: any }) {
	const joyrideRef = useRef<any>(null);
	const [runOnboarding, setRunOnboarding] = useState<boolean>(false);
	const [stepIndex, setStepIndex] = useState<number>(0);

	const pageState: PageState = useSelector((state: AppState) => state.pageState);
	const triggerOnboarding: boolean = useSelector((state: AppState) => state.triggerOnboarding);
	const loggedUser: LoggedUser = useSelector((state: AppState) => state.loggedUser);

	const [openAssignModal, setOpenAssignModal] = useState<boolean>(false);

	const dispatch: Dispatch<any> = useDispatch();

	const currentVariantSidebar = useMemo(
		() => (pageState === PageState.Dashboard ? "hidden" : "visible"),
		[pageState],
	);

	// STEPS
	const INITIAL_STEP: Step = {
		target: "body",
		disableOverlayClose: true,
		disableBeacon: true,
		styles: {
			options: {
				width: "33rem",
			},
		},
		content: (
			<Box>
				<img
					className="onboarding__image"
					src={headerImage}
				/>
				<Typography className="onboarding__title">
					¡Hola! Te damos la bienvenida a Nurasign.
				</Typography>
				<Typography className="onboarding__paragraph">
					En este recorrido, te mostraremos paso a paso cómo usar Nurasign para que puedas
					sacarle el máximo provecho.
				</Typography>
			</Box>
		),
		placement: "center",
		title: <></>,
	};

	const ASSISTANCE_ADJUST_STEPS: Step[] = [
		{
			disableOverlayClose: true,
			disableBeacon: true,
			styles: {
				options: {
					zIndex: 55000,
					width: "30rem",
				},
			},
			spotlightPadding: 0,
			target: "#onboarding-1",
			placement: "right-start",
			title: "El primer paso: hacer tus Ajustes de asistencia",
			content: (
				<Typography className="onboarding__paragraph">
					Aquí podrás hacer tu planificación diaria de la plantilla con control de cambios
					sobre la operación.
				</Typography>
			),
		},
		{
			disableOverlayClose: true,
			disableBeacon: true,
			styles: {
				options: {
					zIndex: 55000,
					width: "30rem",
				},
			},
			target: "#onboarding-2",
			title: "Funciones de Ajustes de asistencia",
			content: (
				<Typography className="onboarding__paragraph">
					Podrás gestionar al personal de Enfermería de manera eficiente: agregar, mover,
					reemplazar y eliminar profesionales del turno según sea necesario.
				</Typography>
			),
		},
		{
			disableBeacon: true,
			disableOverlayClose: true,
			styles: {
				options: {
					zIndex: 10000,
					width: "30rem",
				},
			},
			target: "#onboarding-3",
			title: "Completa tus Ajustes de Asistencia para avanzar",
			content: (
				<Typography className="onboarding__paragraph">
					Antes de acceder al módulo de Pacientes, asegúrate de configurar tus ajustes de
					asistencia. Si no tienes ajustes por realizar, simplemente haz clic en
					Continuar.
				</Typography>
			),
		},
	];

	const PATIENTS_STEPS: Step[] = [
		{
			disableBeacon: true,
			disableOverlayClose: true,
			disableScrolling: true,
			styles: {
				options: {
					zIndex: 55000,
					width: "30rem",
				},
			},
			spotlightPadding: 0,
			target: "#onboarding-4",
			placement: "right",
			title: "Gestión eficiente de pacientes",
			content: (
				<Typography className="onboarding__paragraph">
					En el módulo de Pacientes podrás clasificar y asignar pacientes de manera
					equilibrada al personal de enfermería, optimizando la atención y los recursos.
				</Typography>
			),
		},
		{
			disableBeacon: true,
			disableOverlayClose: true,
			disableScrolling: true,
			styles: {
				options: {
					zIndex: 55000,
					width: "30rem",
				},
			},
			spotlightPadding: 0,
			target: "#onboarding-5",
			placement: "bottom",
			title: "Pacientes sin asignar",
			content: (
				<Typography className="onboarding__paragraph">
					Selecciona un paciente sin asignar (marcado con una pestaña azul) para definir
					su categoría, asignar un profesional y, si es necesario, un profesional
					auxiliar.
				</Typography>
			),
		},
		{
			disableBeacon: true,
			disableOverlayClose: true,
			disableScrolling: true,
			styles: {
				options: {
					zIndex: 55000,
					width: "30rem",
				},
			},

			spotlightPadding: 0,
			target: "#onboarding-6",
			placement: "right",
			title: "Asignar Categoría al paciente",
			content: (
				<Typography className="onboarding__paragraph">
					Consulta el listado de cuidados disponibles y selecciona las opciones que
					apliquen para actualizar automáticamente la categoría del paciente. Usa las
					flechas del teclado para navegar y la tecla Enter para seleccionar.
				</Typography>
			),
		},
		{
			disableBeacon: true,
			disableOverlayClose: true,
			disableScrolling: true,
			styles: {
				options: {
					zIndex: 55000,
					width: "30rem",
				},
			},
			spotlightPadding: 0,
			target: "#onboarding-6",
			placement: "right",
			title: "Asignar Profesional y Profesional Auxiliar al paciente",
			content: (
				<Typography className="onboarding__paragraph">
					Selecciona un profesional adecuado para el paciente según su categoría y la
					carga de trabajo del profesional, garantizando una atención óptima y
					equilibrada.
				</Typography>
			),
		},
		{
			disableBeacon: true,
			disableOverlayClose: true,
			disableScrolling: true,
			styles: {
				options: {
					zIndex: 55000,
					width: "30rem",
				},
			},
			spotlightPadding: 0,
			target: "#onboarding-7",
			placement: "top",
			title: "Cambio de turno",
			content: (
				<Typography className="onboarding__paragraph">
					Para el cambio de turno realiza una actualización de categoría, profesional y
					profesional auxiliar para los pacientes.
				</Typography>
			),
		},
		{
			disableBeacon: true,
			disableOverlayClose: true,
			disableScrolling: true,
			styles: {
				options: {
					zIndex: 55000,
					width: "30rem",
				},
				spotlight: {
					transform: "translateY(-10px)",
				},
			},
			spotlightPadding: 60,
			target: "#onboarding-8",
			placement: "left",
			title: "Completa la asignación de Categoría para avanzar",
			content: (
				<Typography className="onboarding__paragraph">
					Para actualizar el profesional y el profesional auxiliar, primero es necesario
					actualizar o confirmar la categoría.
				</Typography>
			),
		},
	];

	const PLANIFICATION_ASSISTANCE_STEP_COORD: Step = {
		disableBeacon: true,
		disableOverlayClose: true,
		disableScrolling: true,
		styles: {
			options: {
				zIndex: 55000,
				width: "30rem",
			},
		},
		spotlightPadding: 0,
		target: "#onboarding-9",
		placement: "right",
		title: "Visualización de Planificación de Asistencia",
		content: (
			<Typography className="onboarding__paragraph">
				Consulta el calendario de cada profesional para ver sus días laborales y no
				laborales.
			</Typography>
		),
	};

	const PLANIFICATION_ASSISTANCE_STEP_JEFE: Step = {
		disableBeacon: true,
		disableOverlayClose: true,
		disableScrolling: true,
		styles: {
			options: {
				zIndex: 55000,
				width: "30rem",
			},
		},
		spotlightPadding: 0,
		target: "#onboarding-9",
		placement: "right",
		title: "Gestión de Planificación de Asistencia",
		content: (
			<Typography className="onboarding__paragraph">
				Accede y edita los días laborales y no laborales de los profesionales, además de
				subir la plantilla automatizada para actualizar la planificación de asistencia.
			</Typography>
		),
	};

	const NURSIGN_FUNCTIONS_STEPS: Step[] = [
		{
			disableBeacon: true,
			disableOverlayClose: true,
			disableScrolling: true,
			styles: {
				options: {
					zIndex: 55000,
					width: "30rem",
				},
			},
			spotlightPadding: 0,
			target: "#onboarding-10",
			placement: "right",
			title: "Gestión de Funciones de Enfermería",
			content: (
				<Typography className="onboarding__paragraph">
					Consulta el listado de funciones disponibles y asigna tareas específicas a cada
					profesional.
				</Typography>
			),
		},
		{
			disableBeacon: true,
			disableOverlayClose: true,
			disableScrolling: true,
			styles: {
				options: {
					zIndex: 55000,
					width: "30rem",
				},
			},
			target: "#onboarding-11",
			placement: "right",
			title: "Gestión de Funciones Enfermería",
			content: (
				<Typography className="onboarding__paragraph">
					Asigna o elimina funciones a cada profesional.
				</Typography>
			),
		},
	];

	const DASHBOARD_STEPS: Step[] = [
		{
			disableBeacon: true,
			disableOverlayClose: true,
			disableScrolling: true,
			styles: {
				options: {
					zIndex: 55000,
					width: "30rem",
				},
			},
			spotlightPadding: 0,
			target: "#onboarding-12",
			placement: "right",
			title: "Tablero de Asignación diaria",
			content: (
				<Typography className="onboarding__paragraph">
					Consulta el listado de camas con la categoría de cada paciente y los
					profesionales asignados, permitiendo que cada profesional en turno visualice las
					camas que tiene asignadas.
				</Typography>
			),
		},
		{
			disableBeacon: true,
			disableOverlayClose: true,
			disableScrolling: true,
			styles: {
				options: {
					zIndex: 55000,
					width: "30rem",
				},
			},
			spotlightPadding: 0,
			target: "#onboarding-13",
			placement: "top-end",
			title: "Tablero de Asignación diaria",
			content: (
				<Typography className="onboarding__paragraph">
					Utiliza los botones de control en el encabezado para expandir la tabla y obtener
					más espacio. También puedes cerrar el tablero y regresar al módulo anterior con
					facilidad.
				</Typography>
			),
		},
	];

	const STEPS: Step[] = [
		INITIAL_STEP,
		...ASSISTANCE_ADJUST_STEPS,
		...PATIENTS_STEPS,
		...(loggedUser.position === Position.COORD ? [PLANIFICATION_ASSISTANCE_STEP_COORD] : []),
		...(loggedUser.position === Position.JEFE || Position.SUPER
			? [PLANIFICATION_ASSISTANCE_STEP_JEFE]
			: []),
		...NURSIGN_FUNCTIONS_STEPS,
		...DASHBOARD_STEPS,
	];

	const joyrideCallback = (data: CallBackProps) => {
		const {
			action,
			index,
			origin,
			status,
			type,
		}: {
			action: Actions;
			index: number;
			origin: Origin | null;
			status: Status;
			type: Events;
		} = data;

		const finishedStatus: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

		if (action === ACTIONS.CLOSE && origin === ORIGIN.KEYBOARD) {
			setRunOnboarding(false);
			dispatch(setTriggerOnboarding(false));
		}

		if (([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND] as Events[]).includes(type)) {
			const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);

			if (nextIndex === 4) {
				dispatch(setPageState(PageState.Patients));
			} else if (nextIndex === 6) {
				setOpenAssignModal(true);
				setRunOnboarding(false);
				new Promise((resolve) => setTimeout(resolve, 2000)).then(() => {
					setRunOnboarding(true);
					setStepIndex(nextIndex);
				});
				return;
			} else if (nextIndex === 8) {
				setOpenAssignModal(false);
				setRunOnboarding(false);
				new Promise((resolve) => setTimeout(resolve, 500)).then(() => {
					setRunOnboarding(true);
					setStepIndex(nextIndex);
				});
				return;
			} else if (nextIndex === 10) {
				dispatch(setPageState(PageState.PlanificationAssistance));
			} else if (nextIndex === 11) {
				dispatch(setPageState(PageState.NursingFunctions));
			} else if (nextIndex === 14) {
				setRunOnboarding(false);
				dispatch(setHeaderState(HeaderState.Extended));
				dispatch(setPageState(PageState.Dashboard));
				new Promise((resolve) => setTimeout(resolve, 1000)).then(() => {
					setRunOnboarding(true);
					setStepIndex(nextIndex);
				});
				return;
			}
			setStepIndex(nextIndex);
		} else if (finishedStatus.includes(status)) {
			setRunOnboarding(false);
			dispatch(setTriggerOnboarding(false));
		}
	};

	const startOnboarding = () => {
		setRunOnboarding(triggerOnboarding);
		dispatch(setPageState(PageState.AssistanceAdjust));
		setStepIndex(0);
	};

	useEffect(() => {
		if (triggerOnboarding) startOnboarding();
	}, [triggerOnboarding]);

	useEffect(() => {
		const handleKeydow = (event: KeyboardEvent) => {
			if (event.key === "Enter") {
				setStepIndex((prev) => prev + 1);
			}
		};

		if (runOnboarding) {
			window.addEventListener("keydown", handleKeydow);
			return () => window.removeEventListener("keydown", handleKeydow);
		}
		return () => {};
	}, [runOnboarding]);

	const contextValue = {
		joyrideRef,
		joyrideCallback,
		runOnboarding,
		stepIndex,
		steps: STEPS,
		currentVariantSidebar,
		pageState,
		openAssignModal,
	};

	return <HomeContext.Provider value={contextValue}>{children}</HomeContext.Provider>;
}
